import type { PayloadAction } from '@reduxjs/toolkit';
import type { NormalizedV2Response } from '@/types/eds/api/responses/NormalizedV2Response';
import type { AddContactInfoRequest } from '@/types/entities/contactInfo';
import type { AddAddressRequest } from '@/domains/addresses/types/api/requests/AddAddressRequest';
import type { BeneficiaryMakeAClaimantRequest } from '@/domains/beneficiaries/types/api/requests/BeneficiaryMakeAClaimantRequest';
import type { GetBeneficiaryListResponse } from '@/domains/beneficiaries/types/api/responses/GetBeneficiaryListResponse';
import type { HttpServiceResponse } from '@/services/HttpService/types';
import type { DeleteBeneficiaryRequest } from '../types/api/requests/DeleteBeneficiaryRequest';
import { call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import { isAxiosError } from 'axios';
import { EdsEntityCodes } from '@/features/eds/enums/EdsEntityCodes';
import { EdsFilterEntities } from '@/features/eds/enums/EdsFilterEntities';
import EdsAdapterService from '@/features/eds/services/EdsAdapterService/EdsAdapterService';
import EdsDebugService from '@/features/eds/services/EdsDebugService/EdsDebugService';
import { EdsFilterUtilsService } from '@/features/eds/services/EdsFilterUtilsService/EdsFilterUtilsService';
import { getBeneficiaryDetails } from '@/features/entityDetails/store/entityDetailsSlice';
import { resetGeographyState } from '@/features/geography/store/geographySlice';
import { resetAddressValidationState } from '@/features/googleApi/addressValidationApi/store/googleAddressValidationApiSlice';
import { resetGooglePlacesState } from '@/features/googleApi/placesApi/store/googlePlacesApiSlice';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { setSystemLoadingStatus } from '@/features/system/store/systemSlice';
import {
  addBeneficiaryAddress,
  addBeneficiaryContactInfo,
  deleteBeneficiary,
  deleteBeneficiarySuccess,
  generateClaimantAccountPasscode,
  getBeneficiaryList,
  getBeneficiaryListSuccess,
  sendMakeAClaimant,
  sendMakeAClaimantSuccess,
  updateBeneficiary,
  updateBeneficiaryFailed,
  updateBeneficiarySuccess,
} from '@/domains/beneficiaries/store/beneficiarySlice';
import { selectCurrentClaimSetId } from '@/domains/claimSets/store/claimSetSlice';
import { getClaimantsList } from '@/domains/claimants/store/claimantsListSlice';
import { getClaimSetClaimListData } from '@/domains/claims/features/ClaimSetClaimList/store/claimSetClaimList';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import SnacksService from '@/services/SnackService/SnackService';

export function* getBeneficiaryListSaga(action: PayloadAction<{ claimId: string }>) {
  try {
    const { data }: HttpServiceResponse<GetBeneficiaryListResponse> = yield call(
      HttpService.post,
      `/filter/${EdsEntityCodes.Beneficiary}?includes=Party.Person,Party.Business,HelperTags`,
      [
        {
          propertyName: EdsFilterEntities.ClaimId,
          values: [action.payload.claimId],
        },
      ],
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const beneficiaryList: NormalizedV2Response[] = yield call(EdsAdapterService.normalize, data.listings, {
      includeId: true,
    });

    EdsDebugService.logResponse(EdsEntityCodes.Beneficiary, {
      rawResponse: data,
      normalizedResponse: beneficiaryList,
      includedEntitiesRecord: beneficiaryList?.[0]?.entities,
    });

    if (data) {
      yield put(getBeneficiaryListSuccess({ claimId: action.payload.claimId, dataList: beneficiaryList }));
    }
  } catch (error) {
    console.error(error);
  }
}

function* sendBeneficiaryMakeAClaimantSaga({
  payload: { claimId, beneficiaryId, displayName },
}: PayloadAction<BeneficiaryMakeAClaimantRequest>) {
  const claimSetId = yield select(selectCurrentClaimSetId);

  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/claimants/${claimId}/beneficiaries/${beneficiaryId}/designate`, {});
    yield put(sendMakeAClaimantSuccess());
    SnacksService.add({
      type: SnackType.Success,
      title: `Claimant ${displayName} has been added successfully`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    yield put(getBeneficiaryList({ claimId: String(claimId) }));

    const claimantFilterParams = EdsFilterUtilsService.prepareCustomFiltersForSending({
      [EdsFilterEntities.ClaimId]: [`${claimId}`],
    });
    yield put(getClaimantsList({ filters: claimantFilterParams }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(getClaimSetClaimListData({ claimSetId: String(claimSetId) }));
    yield put(setSystemLoadingStatus(false));
  }
}

function* addBeneficiaryContactInfoSaga({ payload }: PayloadAction<AddContactInfoRequest>) {
  const claimSetId = yield select(selectCurrentClaimSetId);

  try {
    yield put(setSystemLoadingStatus(true));
    yield call(HttpService.post, `/examiner/beneficiaries/add-contact-information`, payload);
    yield put(getBeneficiaryDetails({ beneficiaryId: `${payload.entityId}` }));
  } catch (error) {
    console.error(error);
  } finally {
    yield put(getClaimSetClaimListData({ claimSetId: String(claimSetId) }));
    yield put(setSystemLoadingStatus(false));
    ModalService.close(Modals.AddContactInfo);
  }
}

function* addBeneficiaryAddressSaga({ payload }: PayloadAction<AddAddressRequest>) {
  const claimSetId = yield select(selectCurrentClaimSetId);

  try {
    yield put(setSystemLoadingStatus(true));

    yield call(HttpService.post, `/examiner/beneficiaries/add-address`, payload);
    SnacksService.add({
      type: SnackType.Success,
      title: 'Updates saved successfully!',
      anchorOrigin: {
        horizontal: 'right',
        vertical: 'bottom',
      },
    });

    yield put(
      getBeneficiaryDetails({
        beneficiaryId: String(payload.entityId),
      }),
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setSystemLoadingStatus(false));
    yield put(resetGeographyState());
    yield put(resetAddressValidationState());
    yield put(resetGooglePlacesState());
    yield put(getClaimSetClaimListData({ claimSetId: String(claimSetId) }));
    ModalService.close(Modals.AddAddress);
  }
}

function* deleteBeneficiarySaga({
  payload: { claimId, beneficiaryId, beneficiaryName },
}: PayloadAction<DeleteBeneficiaryRequest>) {
  try {
    yield call(HttpService.delete, `/examiner/beneficiaries/${beneficiaryId}`, {});
    yield put(getBeneficiaryList({ claimId: String(claimId) }));

    SnacksService.add({
      type: SnackType.Success,
      title: `Beneficiary ${beneficiaryName} was successfully removed.`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    yield put(deleteBeneficiarySuccess());
  } catch (error) {
    console.error(error);

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to remove a beneficiary. Please try again',
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  }
}

function* generateClaimantAccountPasscodeSaga({
  payload: { beneficiaryId },
}: PayloadAction<{ beneficiaryId: string }>) {
  try {
    const { data } = yield call(HttpService.post, `/account/generate-claimant-passcode/${beneficiaryId}`, {});
    yield put(getBeneficiaryDetails({ beneficiaryId }));

    if (data.warningMessage && typeof data.warningMessage === 'string') {
      SnacksService.add({
        type: SnackType.Warning,
        title: data.warningMessage,
        anchorOrigin: {
          vertical: 'bottom',
          horizontal: 'right',
        },
      });
    }

    SnacksService.add({
      type: SnackType.Success,
      title: `Claimant account passcode was successfully generated.`,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
  } catch (error) {
    console.error(error);
    const errorMessage = isAxiosError(error) ? error.response?.data : 'Please try again';

    SnacksService.add({
      type: SnackType.Error,
      title: 'Failed to generate claimant account passcode',
      content: errorMessage,
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
    });
  }
}

function* updateBeneficiarySaga({ payload }: PayloadAction<string | number>) {
  try {
    const { data } = yield call(
      HttpService.get,
      `/v2/${EdsEntityCodes.Beneficiary}/${payload}?includeEntities=Party.Person,Party.Business,HelperTags`,
      {},
      {
        baseURL: import.meta.env.VITE_API_URL_EDS,
      },
    );

    const normalizedResponse: NormalizedV2Response = yield call(EdsAdapterService.normalize, data);

    yield put(updateBeneficiarySuccess(normalizedResponse));
  } catch (error) {
    yield put(updateBeneficiaryFailed());
    console.error(error);
  }
}

export function* beneficiaryWatcher() {
  yield takeLatest(sendMakeAClaimant.type, sendBeneficiaryMakeAClaimantSaga);
  yield takeEvery([getBeneficiaryList.type], getBeneficiaryListSaga);
  yield takeLatest(addBeneficiaryContactInfo.type, addBeneficiaryContactInfoSaga);
  yield takeLatest(addBeneficiaryAddress.type, addBeneficiaryAddressSaga);
  yield takeLatest(deleteBeneficiary.type, deleteBeneficiarySaga);
  yield takeLatest(generateClaimantAccountPasscode.type, generateClaimantAccountPasscodeSaga);
  yield takeLatest(updateBeneficiary.type, updateBeneficiarySaga);
}
